import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

const RANGES = [
  { code: "24h", label: "近 24 小时" },
  { code: "7d", label: "近 7 天" },
  { code: "30d", label: "近 30 天" },
] as const;

type RangeCode = (typeof RANGES)[number]["code"];

interface CallRow {
  model_code: string;
  provider_code: string | null;
  total: number;
  succeeded: number;
  failed: number;
  canceled: number;
  success_rate: number | null;
  avg_duration_ms: number | null;
}
interface CostRow {
  provider_code: string;
  model_code: string;
  quality_tier: string;
  calls: number;
  credits_consumed: number;
  route_cost_cents: number;
}
interface TopSpenderRow {
  user_id: string;
  username: string;
  phone: string;
  credit_balance: number;
  credits_spent: number;
  transaction_count: number;
}
interface MetricsRes<T> {
  range: string;
  since: string;
  items: T[];
}

export function AdminView() {
  const [range, setRange] = useState<RangeCode>("24h");

  const calls = useQuery({
    queryKey: ["admin", "metrics", "calls", range],
    queryFn: () =>
      apiFetch<MetricsRes<CallRow>>(`/api/v2/admin/metrics/calls?range=${range}`),
  });
  const cost = useQuery({
    queryKey: ["admin", "metrics", "cost", range],
    queryFn: () =>
      apiFetch<MetricsRes<CostRow>>(`/api/v2/admin/metrics/cost?range=${range}`),
  });
  const top = useQuery({
    queryKey: ["admin", "metrics", "top-spenders", range],
    queryFn: () =>
      apiFetch<MetricsRes<TopSpenderRow>>(
        `/api/v2/admin/metrics/top-spenders?range=${range}&limit=10`,
      ),
  });

  return (
    <div className="flex flex-col gap-5 px-6 py-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">后台数据</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            仅 admin 角色可见。所有数据基于 SQLite 实时查询。
          </p>
        </div>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.code}
              type="button"
              onClick={() => setRange(r.code)}
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                r.code === range
                  ? "border-accent bg-accent text-accent-foreground"
                  : "border-border bg-card text-muted-foreground hover:text-foreground",
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <Section title="调用统计（按模型 × 供应商）" isLoading={calls.isLoading} isError={calls.isError}>
        {calls.data && calls.data.items?.length > 0 ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>模型</th>
                <th>供应商</th>
                <th className="text-right">总数</th>
                <th className="text-right">成功</th>
                <th className="text-right">失败</th>
                <th className="text-right">成功率</th>
                <th className="text-right">平均耗时 (ms)</th>
              </tr>
            </thead>
            <tbody>
              {calls.data.items.map((r, i) => (
                <tr key={i}>
                  <td>{r.model_code ?? "-"}</td>
                  <td>{r.provider_code ?? "(未路由)"}</td>
                  <td className="text-right">{r.total}</td>
                  <td className="text-right">{r.succeeded}</td>
                  <td className="text-right">{r.failed}</td>
                  <td className="text-right">{r.success_rate != null ? `${r.success_rate}%` : "-"}</td>
                  <td className="text-right">{r.avg_duration_ms ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyRow text="该时间段内无任务记录。" />
        )}
      </Section>

      <Section title="成本与点数消耗（按路由）" isLoading={cost.isLoading} isError={cost.isError}>
        {cost.data && cost.data.items?.length > 0 ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>供应商</th>
                <th>模型</th>
                <th>质量档</th>
                <th className="text-right">调用次数</th>
                <th className="text-right">点数消耗</th>
                <th className="text-right">路由成本 ($)</th>
              </tr>
            </thead>
            <tbody>
              {cost.data.items.map((r, i) => (
                <tr key={i}>
                  <td>{r.provider_code ?? "-"}</td>
                  <td>{r.model_code ?? "-"}</td>
                  <td>{r.quality_tier ?? "-"}</td>
                  <td className="text-right">{r.calls}</td>
                  <td className="text-right">{r.credits_consumed}</td>
                  <td className="text-right">{(r.route_cost_cents / 100).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyRow text="该时间段内无成功任务。" />
        )}
      </Section>

      <Section title="用户消耗排行（Top 10）" isLoading={top.isLoading} isError={top.isError}>
        {top.data && top.data.items?.length > 0 ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th className="text-right">#</th>
                <th>用户名</th>
                <th>手机号</th>
                <th className="text-right">消耗点数</th>
                <th className="text-right">当前余额</th>
                <th className="text-right">任务数</th>
              </tr>
            </thead>
            <tbody>
              {top.data.items.map((r, i) => (
                <tr key={r.user_id}>
                  <td className="text-right text-muted-foreground">{i + 1}</td>
                  <td>{r.username}</td>
                  <td>{r.phone}</td>
                  <td className="text-right font-semibold">{r.credits_spent}</td>
                  <td className="text-right">{r.credit_balance}</td>
                  <td className="text-right">{r.transaction_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyRow text="该时间段内无消耗记录。" />
        )}
      </Section>
    </div>
  );
}

function Section({
  title,
  children,
  isLoading,
  isError,
}: {
  title: string;
  children: React.ReactNode;
  isLoading: boolean;
  isError: boolean;
}) {
  return (
    <section className="rounded-lg border border-border bg-card p-5">
      <h4 className="mb-3 text-base font-semibold text-foreground">{title}</h4>
      {isLoading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">加载中…</div>
      ) : isError ? (
        <div className="py-8 text-center text-sm text-destructive">
          加载失败（仅 admin 角色可访问）。
        </div>
      ) : (
        children
      )}
    </section>
  );
}

function EmptyRow({ text }: { text: string }) {
  return <div className="py-8 text-center text-sm text-muted-foreground">{text}</div>;
}
